import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import dayjs from "dayjs";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { COMPACT_FORM_MODAL_SX } from "../../utils/formModalStyles";
import { create } from "../../services/PatientConsumablesService";
import { getAllStaff } from "../../services/StaffService";
import { getAllConsumables } from "../../services/ConsumablesService";

const validationSchema = Yup.object({
  resourceName: Yup.string().required("Resource Name is required"),
  quantity: Yup.number()
    .typeError("Quantity must be a number")
    .required("Quantity is required"),
  dateUsed: Yup.date()
    .typeError("Date Used is required")
    .required("Date Used is required"),
  usedBy: Yup.string().required("Used By is required"),
});

const AssignPatientForm = ({ open, onClose, admissionId, patientId }) => {
  const [staffList, setStaffList] = useState([]); // ✅ changed from null
  const [consumablesList, setConsumablesList] = useState([]); // ✅ changed from null

  useEffect(() => {
    fetchAssignedStaff();
    fetchConsumables();
  }, []);

  const fetchAssignedStaff = async () => {
    try {
      const res = await getAllStaff();
      setStaffList(res);
    } catch (error) {
      console.error("Failed to fetch assigned staff", error);
    }
  };

  const fetchConsumables = async () => {
    try {
      const res = await getAllConsumables();
      setConsumablesList(res);
    } catch (error) {
      console.error("Failed to fetch consumables", error);
    }
  };

  const formik = useFormik({
    initialValues: {
      resourceName: "",
      quantity: "",
      dateUsed: dayjs().format("YYYY-MM-DD"),
      usedBy: "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          patientAssignmentId: patientId,
          admissionId: admissionId,
          resourceName: values.resourceName,
          quantity: values.quantity,
          usedBy: values.usedBy,
          dateUsed: dayjs(values.dateUsed).format("YYYY-MM-DDTHH:mm:ss"),
        };
        await create(payload);
        alert("Successfully submitted.");
        resetForm();
        onClose();
      } catch (error) {
        alert("Failed to submit patient consumable data.");
      }
    },
  });

  return (
    <BasicModal open={open} onClose={onClose} sx={COMPACT_FORM_MODAL_SX}>
      <Box>
        <Typography
          variant="h5"
          sx={{ fontSize: "1.5rem", fontWeight: "bold", textAlign: "center" }}
          gutterBottom
        >
          Patient Consumables
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                name="resourceName"
                label="Resource Name"
                value={formik.values.resourceName}
                onChange={(e) => {
                  const resourceId = e.target.value;
                  formik.setFieldValue("resourceName", resourceId);
                  formik.setFieldTouched("resourceName", true);
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.resourceName && Boolean(formik.errors.resourceName)
                }
                helperText={
                  formik.touched.resourceName && formik.errors.resourceName
                }
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        overflowY: "auto",
                      },
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  },
                }}
              >
                {(consumablesList ?? []).map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>  
                ))}
              </TextField>
            </Grid>

            {/* Quantity */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="quantity"
                name="quantity"
                label="Quantity"
                value={formik.values.quantity}
                onChange={formik.handleChange}
                error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                helperText={formik.touched.quantity && formik.errors.quantity}
              />
            </Grid>

            {/* Date Used */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                id="dateUsed"
                name="dateUsed"
                label="Date Used"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formik.values.dateUsed}
                onChange={formik.handleChange}
                error={formik.touched.dateUsed && Boolean(formik.errors.dateUsed)}
                helperText={formik.touched.dateUsed && formik.errors.dateUsed}
              />
            </Grid>

            {/* Used By */}
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                id="usedBy"
                name="usedBy"
                label="Used By"
                value={formik.values.usedBy}
                onChange={formik.handleChange}
                error={formik.touched.usedBy && Boolean(formik.errors.usedBy)}
                helperText={formik.touched.usedBy && formik.errors.usedBy}
                SelectProps={{
                  MenuProps: {
                    PaperProps: { style: { maxHeight: 200, overflowY: "auto" } },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    getContentAnchorEl: null,
                  },
                }}
              >
                {staffList
                  .filter((staff) => staff.userRole === "staff")
                  .map((staff) => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <FormModalActions
                onCancel={onClose}
                submitType="submit"
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};

export default AssignPatientForm;